import {
  getViewerPreferences,
  type MinimapPreference,
  updateViewerPreferences,
} from 'features/knowledgeDocumentViewer/utils/viewerPreferencesStorage'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useState } from 'react'

interface UseMinimapPreferenceParams {
  isZoomed: boolean
  isContentReady: boolean
}

export function useMinimapPreference({
  isZoomed,
  isContentReady,
}: UseMinimapPreferenceParams) {
  const { user } = useGeckoPermissions()
  const userId = user?.id

  const [preference, setPreference] = useState<MinimapPreference>(() => {
    if (!userId) return 'auto'
    return getViewerPreferences(userId)?.minimapPreference ?? 'auto'
  })

  const toggleMinimap = useCallback(() => {
    setPreference((prev) => {
      const next: MinimapPreference =
        prev === 'dismissed'
          ? 'forced'
          : prev === 'forced'
            ? 'dismissed'
            : isZoomed
              ? 'dismissed'
              : 'forced'
      if (userId) {
        updateViewerPreferences(userId, { minimapPreference: next })
      }
      return next
    })
  }, [isZoomed, userId])

  const showMinimap =
    preference !== 'dismissed' &&
    (preference === 'forced' || isZoomed) &&
    isContentReady

  const resetPreference = useCallback(() => {
    setPreference('auto')
  }, [])

  return { showMinimap, toggleMinimap, resetPreference }
}
